import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, Clock, Star, Users, Play, CheckCircle, Lock, Heart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';

const CourseDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { getCourseById, getCourseBySlug, enrollInCourse, loadEnrolledCourses, toggleFavorite, enrolledCourses, favorites } = useCourse();

  const [isEnrolling, setIsEnrolling] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

  // UUID slug → lookup by id directly; slug fallback for legacy numeric slugs
  const course = getCourseBySlug(slug) || getCourseById(slug);
  
  if (!course) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Cours non trouvé</h1>
          <p className="text-gray-600 mb-6">Le cours que vous recherchez n'existe pas.</p>
          <button 
            onClick={() => navigate('/courses')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            Retour aux cours
          </button>
        </div>
      </div>
    );
  }

  // Vérifier si l'utilisateur est déjà inscrit au cours
  const isEnrolled = enrolledCourses.some(ec => ec.id === course.id);
  const isFavorite = favorites.includes(course.id);

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setIsEnrolling(true);
    try {
      const result = await enrollInCourse(course.id);
      if (result.success) {
        navigate(`/course/${course.id}/learn`);
      } else if (result.alreadyEnrolled) {
        // Déjà inscrit mais état non synchronisé — recharger et accéder au cours
        await loadEnrolledCourses();
        navigate(`/course/${course.id}/learn`);
      }
    } finally {
      setIsEnrolling(false);
    }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setIsAddingToCart(true);
    try {
      // Simuler l'ajout au panier
      await new Promise(resolve => setTimeout(resolve, 1000));
      console.log('🛒 Cours ajouté au panier:', course.title);
      // Ici vous pourriez afficher une notification de succès
    } catch (error) {
      console.error('💥 Erreur lors de l\'ajout au panier:', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setIsTogglingFavorite(true);
    try {
      const result = await toggleFavorite(course.id);
      if (result.success) {
        console.log(isFavorite ? '💔 Cours retiré des favoris' : '❤️ Cours ajouté aux favoris');
      }
    } catch (error) {
      console.error('💥 Erreur lors du toggle favori:', error);
    } finally {
      setIsTogglingFavorite(false);
    }
  };


  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header du cours */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
          <div className="md:flex">
            <div className="md:w-2/3 p-8">
              <div className="mb-4">
                <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                  {course.category}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-text mb-4">
                {course.title}
              </h1>
              
              <p className="text-lg text-gray-600 mb-6">
                {course.description}
              </p>
              
              <div className="flex items-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400 fill-current" />
                  <span className="font-medium">{course.rating}</span>
                  <span className="text-gray-500">({course.students} avis)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-500" />
                  <span className="text-gray-500">{course.students} étudiants inscrits</span>
                </div>
              </div>
              
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{course.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  <span>{course.language}</span>
                </div>
                {course.certificate && (
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Certificat inclus</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="md:w-1/3 bg-gray-50 p-8">
              <div className="sticky top-24">
                <img
                  src={course.thumbnail}
                  alt={course.title}
                  className="w-full h-48 object-cover rounded-lg mb-6"
                />
                
                <div className="text-center mb-6">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-3xl font-bold text-primary">€{course.price}</span>
                    {course.originalPrice > course.price && (
                      <span className="text-xl text-gray-500 line-through">€{course.originalPrice}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{course.access}</p>
                </div>
                
                {isEnrolled ? (
                  <button
                    onClick={() => navigate(`/course/${course.id}/learn`)}
                    className="w-full bg-green-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors duration-200 mb-4"
                  >
                    Continuer l'apprentissage
                  </button>
                ) : (
                  <button 
                    onClick={handleEnroll}
                    disabled={isEnrolling}
                    className="w-full bg-primary text-white py-3 px-6 rounded-lg font-semibold hover:bg-primary/90 transition-colors duration-200 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEnrolling ? (
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Inscription en cours...
                      </div>
                    ) : (
                      'S\'inscrire maintenant'
                    )}
                  </button>
                )}
                
                <button 
                  onClick={handleAddToCart}
                  disabled={isAddingToCart || isEnrolled}
                  className="w-full border border-primary text-primary py-3 px-6 rounded-lg font-semibold hover:bg-primary hover:text-white transition-colors duration-200 mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAddingToCart ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                      Ajout en cours...
                    </div>
                  ) : (
                    'Ajouter au panier'
                  )}
                </button>

                <button 
                  onClick={handleToggleFavorite}
                  disabled={isTogglingFavorite}
                  className={`w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg font-medium transition-colors duration-200 ${
                    isFavorite 
                      ? 'bg-red-50 text-red-600 hover:bg-red-100' 
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
                  {isTogglingFavorite ? '...' : (isFavorite ? 'Retiré des favoris' : 'Ajouter aux favoris')}
                </button>
                
                <p className="text-xs text-gray-500 text-center mt-4">
                  Garantie de remboursement de 30 jours
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contenu du cours */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-8">
            {/* Ce que vous apprendrez */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-text mb-4">
                Ce que vous apprendrez
              </h2>
              <div className="grid md:grid-cols-2 gap-3">
                {(course.whatYouWillLearn || []).map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Prérequis */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-text mb-4">
                Prérequis
              </h2>
              <ul className="space-y-2">
                {(course.requirements || []).map((requirement, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Description détaillée */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-text mb-4">
                Description du cours
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Ce cours complet de développement web full-stack vous emmènera dans un voyage 
                passionnant à travers toutes les étapes de création d'une application web moderne. 
                Vous commencerez par les bases du HTML et CSS, puis progresserez vers JavaScript 
                et React pour le frontend, et enfin Node.js et MongoDB pour le backend.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                Chaque section est conçue pour être pratique et interactive, avec de nombreux 
                exercices et projets concrets qui vous permettront de mettre en pratique vos 
                nouvelles compétences. À la fin de ce cours, vous serez capable de créer des 
                applications web complètes et professionnelles.
              </p>
            </div>
          </div>

          {/* Programme du cours */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-bold text-text mb-4">
                Programme du cours
              </h2>
              <div className="space-y-3">
                {(course.lessons || []).map((lesson) => (
                  <div key={lesson.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3">
                      {lesson.isFree ? (
                        <Play className="w-4 h-4 text-green-500" />
                      ) : (
                        <Lock className="w-4 h-4 text-gray-400" />
                      )}
                      <div>
                        <h3 className={`text-sm font-medium ${lesson.isFree ? 'text-text' : 'text-gray-500'}`}>
                          {lesson.title}
                        </h3>
                        <p className="text-xs text-gray-500">{lesson.duration}</p>
                      </div>
                    </div>
                    {lesson.isFree && (
                      <span className="text-xs text-green-600 font-medium">Gratuit</span>
                    )}
                  </div>
                ))}
              </div>
              
              <button className="w-full mt-4 text-primary hover:text-primary/80 text-sm font-medium">
                Voir tout le programme
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail;
